import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../hooks/useTheme";
import AppSidebar from "./AppSidebar";
import { RootState } from "../store";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import { fetchUserProfile } from "../store/slices/userSlice";
import { useAppDispatch } from "../store/hooks";
import AnimatedNumber from "./AnimatedNumber";
import ThemeSelector from "./ThemeSelector";
import LanguageSelector from "./LanguageSelector";
import { trackPageVisit } from "../utils/analytics";

const getInitials = (
  name?: string | null,
  username?: string | null,
): string => {
  if (name)
    return name
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  if (username) return username.charAt(0).toUpperCase();
  return "U";
};

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, setTheme } = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.user.profile);
  const streakSpark = useSelector((state: RootState) => state.user.streakSpark);
  const todos = useSelector((state: RootState) => state.todos.items);
  const habits = useSelector((state: RootState) => state.habits.items);
  const notes = useSelector((state: RootState) => state.notes.items);
  const reminders = useSelector((state: RootState) => state.reminders.items);
  const trackers = useSelector((state: RootState) => state.trackers.items);

  // Ref to prevent multiple API calls
  const hasFetchedProfile = useRef(false);

  // Get spark points from either user profile or streakSpark data
  const sparkPoints = user?.sparkPoints || streakSpark?.totalSparkPoints || 0;
  const initials = getInitials(user?.name, user?.username);

  const handleThemeToggle = () => {
    setTheme(isDark ? "light" : "dark");
  };

  const isNavActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const mobileNavLinks = [
    { path: "/app", emoji: "📅", label: t("nav.calendar") },
    { path: "/habits", emoji: "🔥", label: t("nav.habits") },
    { path: "/todos", emoji: "✅", label: t("nav.todos") },
    { path: "/trackers", emoji: "📊", label: t("nav.trackers") },
    { path: "/notes", emoji: "📝", label: t("nav.notes") },
  ];

  // Fetch user profile on component mount
  useEffect(() => {
    trackPageVisit("profile");
    if (!hasFetchedProfile.current) {
      hasFetchedProfile.current = true;
      dispatch(fetchUserProfile());
    }
  }, [dispatch]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{t("profile.pleaseLogin")}</p>
      </div>
    );
  }

  const completedTodos = todos.filter((todo) => todo.completed).length;
  const activeHabits = habits.filter((habit) => habit.isActive).length;
  const activeTrackers = trackers.filter((tracker) => tracker.isActive).length;

  return (
    <>
      <MetaHead
        title={t("profile.title")}
        description={t("profile.description") as string}
        keywords={t("profile.keywords") as string}
      />
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* DESKTOP SIDEBAR */}
        <AppSidebar showQuickActions={false} />

        {/* MAIN CONTENT */}
        <main className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* MOBILE TOPBAR */}
          <header className="lg:hidden flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={() => navigate("/app")}
              aria-label="Go back"
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#3b5cde]/20 border border-[#3b5cde]/30 flex items-center justify-center transition-all hover:bg-[#3b5cde]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
              {t("profile.titleShort")}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleThemeToggle}
                aria-label={
                  isDark
                    ? (t("theme.switchToLight") as string)
                    : (t("theme.switchToDark") as string)
                }
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 bg-black/[0.04] dark:bg-white/[0.05] text-gray-500 dark:text-white/50 text-[19px] flex items-center justify-center cursor-pointer transition-all hover:bg-black/10 dark:hover:bg-white/10"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
              <button
                onClick={() => navigate("/profile")}
                aria-label="Go to profile"
                className="w-8 h-8 rounded-full bg-[#3b5cde] flex items-center justify-center text-[16px] font-semibold text-white"
              >
                {initials}
              </button>
            </div>
          </header>

          {/* DESKTOP PAGE HEADER */}
          <div className="hidden lg:flex items-center gap-4 px-6 py-5 border-b border-black/[0.07] dark:border-white/[0.07]">
            <button
              onClick={() => navigate("/app")}
              aria-label="Go back"
              className="tap-scale w-9 h-9 rounded-xl bg-[#3b5cde]/15 border border-[#3b5cde]/25 flex items-center justify-center transition-all hover:bg-[#3b5cde]/25"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
              {t("profile.titleShort")}
            </h1>
          </div>

          {/* SCROLLABLE CONTENT */}
          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4">
            <div className="max-w-4xl mx-auto pb-8">
              {/* User Info Card */}
              <div className="glass-card p-4 lg:p-5 mb-4 flex items-center gap-4">
                <div className="w-14 h-14 bg-[#3b5cde] rounded-full flex items-center justify-center shrink-0">
                  <span className="text-[21px] font-semibold text-white">
                    {user.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-tight truncate">
                    {user.name}
                  </p>
                  <p className="text-[17px] text-gray-400 dark:text-white/40 mt-0.5">
                    @{user.username}
                  </p>
                  <p className="text-[16px] text-gray-400 dark:text-white/30 mt-0.5 truncate">
                    {user.email}
                  </p>
                </div>
              </div>

              {/* Spark Points Card */}
              <div className="glass-card p-4 lg:p-5 mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#f5a623]/20 border border-[#f5a623]/20 flex items-center justify-center shrink-0">
                    <svg className="w-6 h-6" fill="#f5a623" viewBox="0 0 24 24">
                      <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-[21px] lg:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
                      <AnimatedNumber
                        value={sparkPoints}
                        formatter={(val) => Math.round(val).toLocaleString()}
                        duration={1500}
                      />
                    </p>
                    <p className="text-[13px] lg:text-[14px] uppercase tracking-wider text-gray-400 dark:text-white/30 mt-0.5">
                      {t("profile.sparkPoints")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Stats Grid — 4-column matches styleguide stats card pattern */}
              <p className="text-[13px] font-medium uppercase tracking-widest text-gray-400 dark:text-white/25 mb-2">
                {t("profile.statsTitle")}
              </p>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-3 mb-4">
                {/* Todos */}
                <div className="glass-card p-3 lg:p-4 text-center">
                  <div className="w-9 h-9 bg-[#378ADD]/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <svg
                      className="w-5 h-5 text-[#378ADD]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="text-[21px] lg:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                    <AnimatedNumber
                      value={`${completedTodos}/${todos.length}`}
                      duration={1200}
                    />
                  </div>
                  <div className="text-[13px] lg:text-[14px] uppercase tracking-wider text-gray-400 dark:text-white/30 mt-0.5">
                    {t("profile.todosCompleted")}
                  </div>
                </div>

                {/* Habits */}
                <div className="glass-card p-3 lg:p-4 text-center">
                  <div className="w-9 h-9 bg-[#f5a623]/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <svg
                      className="w-5 h-5 text-[#f5a623]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <div className="text-[21px] lg:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                    <AnimatedNumber value={activeHabits} duration={1000} />
                  </div>
                  <div className="text-[13px] lg:text-[14px] uppercase tracking-wider text-gray-400 dark:text-white/30 mt-0.5">
                    {t("profile.activeHabits")}
                  </div>
                </div>

                {/* Notes */}
                <div className="glass-card p-3 lg:p-4 text-center">
                  <div className="w-9 h-9 bg-[#22c67a]/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <svg
                      className="w-5 h-5 text-[#22c67a]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </div>
                  <div className="text-[21px] lg:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                    <AnimatedNumber value={notes.length} duration={1100} />
                  </div>
                  <div className="text-[13px] lg:text-[14px] uppercase tracking-wider text-gray-400 dark:text-white/30 mt-0.5">
                    {t("profile.notes")}
                  </div>
                </div>

                {/* Reminders */}
                <div className="glass-card p-3 lg:p-4 text-center">
                  <div className="w-9 h-9 bg-[#E23F5D]/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <svg
                      className="w-5 h-5 text-[#E23F5D]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="text-[21px] lg:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                    <AnimatedNumber value={reminders.length} duration={1300} />
                  </div>
                  <div className="text-[13px] lg:text-[14px] uppercase tracking-wider text-gray-400 dark:text-white/30 mt-0.5">
                    {t("profile.reminders")}
                  </div>
                </div>

                {/* Trackers */}
                <div className="glass-card p-3 lg:p-4 text-center">
                  <div className="w-9 h-9 bg-[#a33fe8]/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <svg
                      className="w-5 h-5 text-[#a33fe8]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                  <div className="text-[21px] lg:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                    <AnimatedNumber value={activeTrackers} duration={1400} />
                  </div>
                  <div className="text-[13px] lg:text-[14px] uppercase tracking-wider text-gray-400 dark:text-white/30 mt-0.5">
                    {t("profile.activeTrackers")}
                  </div>
                </div>

                {/* Member Since */}
                <div className="glass-card p-3 lg:p-4 text-center">
                  <div className="w-9 h-9 bg-black/[0.06] dark:bg-white/[0.06] rounded-lg flex items-center justify-center mx-auto mb-2">
                    <svg
                      className="w-5 h-5 text-gray-400 dark:text-white/40"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div className="text-[17px] font-semibold tracking-tight text-gray-900 dark:text-white">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </div>
                  <div className="text-[13px] lg:text-[14px] uppercase tracking-wider text-gray-400 dark:text-white/30 mt-0.5">
                    {t("profile.memberSince")}
                  </div>
                </div>
              </div>

              {/* Preferences Section */}
              <p className="text-[13px] font-medium uppercase tracking-widest text-gray-400 dark:text-white/25 mb-2">
                {t("profile.preferences")}
              </p>
              <div className="glass-card p-4 lg:p-5">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-[16px] font-medium text-gray-500 dark:text-white/40 mb-2">
                      {t("profile.theme")}
                    </label>
                    <ThemeSelector />
                  </div>
                  <div className="flex-1">
                    <label className="block text-[16px] font-medium text-gray-500 dark:text-white/40 mb-2">
                      {t("profile.language")}
                    </label>
                    <LanguageSelector />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* MOBILE BOTTOM NAV */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#f7f6f3]/95 dark:bg-[#0f0f12]/95 backdrop-blur-sm border-t border-black/[0.08] dark:border-white/[0.08]">
          <div className="flex items-center justify-around px-2 pt-2 pb-2">
            {mobileNavLinks.map(({ path, emoji, label }) => {
              const active = isNavActive(path);
              return (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="flex flex-col items-center gap-1 cursor-pointer"
                  style={{ color: active ? "#f5a623" : undefined }}
                >
                  <span className="text-[22px]">{emoji}</span>
                  <span
                    className={`text-[13px] font-medium${active ? "" : " text-gray-400 dark:text-white/25"}`}
                    style={active ? { color: "#f5a623" } : undefined}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </>
  );
};

export default Profile;
